import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Calendar as CalIcon, MapPin, Clock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function CalendarSection() {
  const [events, setEvents] = useState([]);
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", school: "", role: "" });
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    api
      .get("/events")
      .then((r) => {
        setEvents(r.data);
        // Auto-jump to month of earliest upcoming event
        const today = isoDate(new Date());
        const upcoming = r.data.filter((e) => e.date >= today).sort((a, b) => a.date.localeCompare(b.date));
        if (upcoming.length > 0) {
          const d = new Date(upcoming[0].date + "T00:00:00");
          setCursor(new Date(d.getFullYear(), d.getMonth(), 1));
        }
      })
      .catch(() => {});
  }, []);

  const eventsByDate = useMemo(() => {
    const map = {};
    for (const e of events) {
      (map[e.date] = map[e.date] || []).push(e);
    }
    return map;
  }, [events]);

  const month = cursor.getMonth();
  const year = cursor.getFullYear();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const selectedEvent = events.find((e) => e.id === selectedEventId) || null;

  const onSubmitRsvp = async (e) => {
    e.preventDefault();
    if (!selectedEvent) return;
    setSubmitting(true);
    setStatus(null);
    try {
      await api.post("/rsvp", { event_id: selectedEvent.id, ...form });
      setStatus({ ok: true, msg: "Kia ora! Your RSVP is confirmed. We'll be in touch." });
      setForm({ name: "", email: "", school: "", role: "" });
    } catch (err) {
      setStatus({ ok: false, msg: formatApiError(err.response?.data?.detail) || err.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="calendar" className="section bg-[var(--sage)]">
      <div className="container-pl">
        <div className="max-w-2xl">
          <div className="overline">Upcoming sessions</div>
          <h2 className="font-display font-bold text-3xl md:text-5xl mt-3 leading-tight">
            Info sessions & cohort dates
          </h2>
          <p className="mt-5 text-[var(--slate)] text-base md:text-lg">
            Browse upcoming information sessions and reserve your place. Spaces are limited.
          </p>
        </div>

        <div className="mt-12 grid lg:grid-cols-12 gap-8">
          {/* Calendar */}
          <div className="lg:col-span-7 card" data-testid="calendar-card">
            <div className="flex items-center justify-between">
              <button
                onClick={() => setCursor(new Date(year, month - 1, 1))}
                className="p-2 rounded-md hover:bg-[var(--sage)] text-[var(--forest)]"
                data-testid="calendar-prev-btn"
                aria-label="Previous month"
              >
                <ChevronLeft size={20} />
              </button>
              <div className="font-display font-semibold text-xl" data-testid="calendar-month-label">
                {MONTHS[month]} {year}
              </div>
              <button
                onClick={() => setCursor(new Date(year, month + 1, 1))}
                className="p-2 rounded-md hover:bg-[var(--sage)] text-[var(--forest)]"
                data-testid="calendar-next-btn"
                aria-label="Next month"
              >
                <ChevronRight size={20} />
              </button>
            </div>

            <div className="cal-grid mt-6">
              {DOW.map((d) => (
                <div key={d} className="cal-dow">{d}</div>
              ))}
              {cells.map((d, i) => {
                if (d === null) return <div key={i} className="cal-cell empty" />;
                const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
                const hasEvent = !!eventsByDate[dateStr];
                const isSelected = hasEvent && eventsByDate[dateStr].some((e) => e.id === selectedEventId);
                return (
                  <div
                    key={i}
                    className={`cal-cell ${hasEvent ? "has-event" : ""} ${isSelected ? "selected" : ""}`}
                    onClick={() => hasEvent && setSelectedEventId(eventsByDate[dateStr][0].id)}
                    data-testid={`calendar-day-${dateStr}`}
                  >
                    {d}
                  </div>
                );
              })}
            </div>

            <div className="mt-6 flex flex-wrap gap-3 text-xs text-[var(--slate)]">
              <span className="inline-flex items-center gap-2">
                <span className="w-3 h-3 rounded-sm bg-white border border-[var(--forest)]" /> Has event
              </span>
              <span className="inline-flex items-center gap-2">
                <span className="w-3 h-3 rounded-sm bg-[var(--forest)]" /> Selected
              </span>
            </div>
          </div>

          {/* Event details + RSVP */}
          <div className="lg:col-span-5 card" data-testid="rsvp-card">
            {events.length === 0 ? (
              <div className="text-[var(--slate)]" data-testid="rsvp-empty-state">
                <CalIcon className="text-[var(--forest)] mb-3" size={26} />
                <h3 className="font-display font-semibold text-lg">No sessions scheduled yet</h3>
                <p className="mt-2 text-sm">
                  Sessions for the Term 4 cohort will appear here soon. Enrol below
                  to be the first to know.
                </p>
              </div>
            ) : !selectedEvent ? (
              <div className="text-[var(--slate)]" data-testid="rsvp-select-prompt">
                <CalIcon className="text-[var(--forest)] mb-3" size={26} />
                <h3 className="font-display font-semibold text-lg">Select an event on the calendar</h3>
                <p className="mt-2 text-sm">
                  Click any highlighted date to see details and reserve your place.
                </p>

                <div className="mt-6">
                  <div className="overline">Upcoming</div>
                  <ul className="mt-3 space-y-2">
                    {[...events]
                      .sort((a, b) => a.date.localeCompare(b.date))
                      .slice(0, 5)
                      .map((e) => (
                        <li key={e.id}>
                          <button
                            onClick={() => setSelectedEventId(e.id)}
                            className="text-left w-full p-3 rounded-lg hover:bg-[var(--sage)] border border-transparent hover:border-[var(--border)] transition"
                            data-testid={`upcoming-event-${e.id}`}
                          >
                            <div className="font-display font-semibold text-[var(--charcoal)]">{e.title}</div>
                            <div className="text-xs text-[var(--slate)] mt-1">
                              {e.date} · {e.time}
                            </div>
                          </button>
                        </li>
                      ))}
                  </ul>
                </div>
              </div>
            ) : (
              <div data-testid="rsvp-event-details">
                <div className="overline">{selectedEvent.date}</div>
                <h3 className="font-display font-bold text-2xl mt-1">{selectedEvent.title}</h3>
                <div className="mt-3 flex flex-wrap gap-4 text-sm text-[var(--slate)]">
                  <span className="inline-flex items-center gap-1.5">
                    <Clock size={15} /> {selectedEvent.time}
                  </span>
                  {selectedEvent.location && (
                    <span className="inline-flex items-center gap-1.5">
                      <MapPin size={15} /> {selectedEvent.location}
                    </span>
                  )}
                </div>
                {selectedEvent.description && (
                  <p className="mt-4 text-[var(--slate)] text-sm leading-relaxed">
                    {selectedEvent.description}
                  </p>
                )}

                <form onSubmit={onSubmitRsvp} className="mt-6 space-y-4" data-testid="rsvp-form">
                  <div className="field">
                    <label htmlFor="rsvp-name">Full name</label>
                    <input
                      id="rsvp-name"
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      data-testid="rsvp-name-input"
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="rsvp-email">Email</label>
                    <input
                      id="rsvp-email"
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      data-testid="rsvp-email-input"
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="field">
                      <label htmlFor="rsvp-school">School</label>
                      <input
                        id="rsvp-school"
                        value={form.school}
                        onChange={(e) => setForm({ ...form, school: e.target.value })}
                        data-testid="rsvp-school-input"
                      />
                    </div>
                    <div className="field">
                      <label htmlFor="rsvp-role">Role</label>
                      <input
                        id="rsvp-role"
                        value={form.role}
                        onChange={(e) => setForm({ ...form, role: e.target.value })}
                        data-testid="rsvp-role-input"
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    className="btn btn-primary w-full"
                    disabled={submitting}
                    data-testid="rsvp-submit-button"
                  >
                    {submitting ? "Reserving..." : "Reserve my place"}
                  </button>
                  {status && (
                    <div
                      className={`text-sm ${status.ok ? "text-[var(--forest)]" : "text-red-600"}`}
                      data-testid="rsvp-status-message"
                    >
                      {status.msg}
                    </div>
                  )}
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
